/** Card: a tinted, hatched frame holding a header row and one or more white bodies. */
import type { ComponentProps, CSSProperties, ReactNode } from "react";
import { cx } from "./cx";

const HATCH_W = 1639;
const HATCH_H = 560;
const HATCH_STEP = 10.777;
const HATCH_RUN = 290.827;

/** The hatch: 126 parallel hairlines leaning right, the same drawing behind every card. An image, so the page cannot select, drag or restyle it. */
const lines = Array.from({ length: 126 }, (_, i) => `M${(i * HATCH_STEP).toFixed(3)} ${HATCH_H}L${(i * HATCH_STEP + HATCH_RUN).toFixed(3)} 0`).join(" ");
const HATCH = `data:image/svg+xml,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="${HATCH_W}" height="${HATCH_H}" viewBox="0 0 ${HATCH_W} ${HATCH_H}" fill="none"><path stroke="black" stroke-opacity=".03" stroke-width=".8" d="${lines}"/></svg>`,
)}`;

interface CardProps extends ComponentProps<"section"> {
  /** Where the hatch sits behind the frame (`left`, `top`, `transform`), so neighbouring cards do not repeat the same slice. */
  hatchStyle?: CSSProperties;
}

export function Card({ className, hatchStyle, children, ...props }: CardProps) {
  return (
    <section className={cx("group/card relative flex flex-col items-start overflow-clip rounded-xl bg-muted p-1 shadow-frame", className)} {...props}>
      <img alt="" src={HATCH} width={HATCH_W} height={HATCH_H} className="pointer-events-none absolute block h-[560px] w-[1639px] max-w-none" style={hatchStyle} />
      {children}
    </section>
  );
}

export function CardHeader({ className, ...props }: ComponentProps<"div">) {
  // 44 px tall: a 16 px title sits in 12 × 14 of padding, and a 36 px control (a Kebab, a select) fits without growing the row.
  return <div className={cx("relative flex min-h-11 w-full shrink-0 items-center justify-between gap-3 px-3 py-1", className)} {...props} />;
}

export function CardTitle({ className, children, ...props }: ComponentProps<"h2">) {
  return (
    <h2 className={cx("whitespace-nowrap text-title font-medium leading-none text-foreground", className)} {...props}>
      {children}
    </h2>
  );
}

const WELL = {
  /** A KPI's: 36 × 36 for a 20 px icon; the card is one link, so the well turns a little when it is hovered. */
  md: "size-9 rounded-[10px] transition-transform duration-200 ease-out-smooth group-hover/card:-rotate-12 group-hover/card:scale-110",
  /** Beside a card title: 28 × 28 for a 16 px icon, still. */
  sm: "size-7 rounded-lg",
};

/** An icon well: a brand icon on the strong brand tint. Every card's icon stands in one, so the cards carry one quiet hue. */
export function CardWell({ size = "md", children }: { size?: keyof typeof WELL; children: ReactNode }) {
  return <span className={cx("flex shrink-0 items-center justify-center bg-brand-tint-strong", WELL[size])}>{children}</span>;
}

const BODY = {
  /** A column of content: charts, feeds, forms. */
  panel: "flex-col items-start overflow-clip rounded-xl border-input px-4 pb-4 pt-5",
  /** A single figure; lifts when the card is hovered. */
  tile: "items-end justify-between rounded-[10px] border-border px-4 pb-3 transition-[box-shadow,transform] duration-200 ease-out-smooth group-hover/card:-translate-y-px group-hover/card:shadow-lift",
};

export function CardBody({ variant = "panel", className, ...props }: ComponentProps<"div"> & { variant?: keyof typeof BODY }) {
  return <div className={cx("relative flex min-h-px w-full flex-1 border-hair bg-card", BODY[variant], className)} {...props} />;
}
